import {
  FilterIcon,
  TableIcon,
  ClockIcon,
  SyncIcon,
  BellIcon,
  CheckCircleIcon,
  XCircleIcon,
  StopIcon,
  AlertIcon,
  SkipIcon,
} from '@primer/octicons-react'
import {ActionList, IconButton, CounterLabel, ActionMenu, PortalContext, registerPortalRoot} from '@primer/react'
import {useMediaQuery} from '@github-ui/use-media-query'

import type {ComponentType} from 'react'
import {useMemo, useCallback, useRef, memo} from 'react'
import type {JobStatusFilterValue} from '../types'

// Export StatusCounts type for parent component to use
export type StatusCounts = Record<JobStatusFilterValue, number>

interface JobStatusFilterProps {
  selectedFilter: JobStatusFilterValue
  onFilterChange: (filter: JobStatusFilterValue) => void
  // Pre-calculated counts from parent to avoid recalculating on every render
  statusCounts: StatusCounts
}

interface StatusOption {
  value: JobStatusFilterValue
  label: string
  icon: ComponentType<{className?: string}>
}

// Grouped status options for rendering with dividers
const pendingStatuses: StatusOption[] = [
  {value: 'queued', label: 'Queued', icon: ClockIcon},
  {value: 'in_progress', label: 'In progress', icon: SyncIcon},
  {value: 'action_required', label: 'Action required', icon: BellIcon},
]

const successStatuses: StatusOption[] = [{value: 'success', label: 'Successful', icon: CheckCircleIcon}]

const failureStatuses: StatusOption[] = [
  {value: 'failure', label: 'Failed', icon: XCircleIcon},
  {value: 'cancelled', label: 'Cancelled', icon: StopIcon},
  {value: 'skipped', label: 'Skipped', icon: SkipIcon},
  {value: 'timed_out', label: 'Timed out', icon: AlertIcon},
]

// All status options flattened for external use
export const statusOptions: Array<{value: JobStatusFilterValue; label: string}> = [
  {value: 'all', label: 'All'},
  ...pendingStatuses,
  ...successStatuses,
  ...failureStatuses,
]

// Unique name for the portal container to avoid conflicts
const PORTAL_CONTAINER_NAME = 'job-status-filter-portal'

/**
 * Job status filter dropdown component.
 * Wrapped in React.memo since it receives pre-calculated statusCounts
 * and only needs to re-render when the selected filter or counts change.
 */
// eslint-disable-next-line @typescript-eslint/no-shadow -- named function expression intentionally shares the outer name
export const JobStatusFilter = memo(function JobStatusFilter({
  selectedFilter,
  onFilterChange,
  statusCounts,
}: JobStatusFilterProps) {
  // Detect if we're in narrow viewport where the jobs list is rendered in a dialog
  // Primer's narrow viewport range
  const isNarrow = useMediaQuery('(max-width: calc(48rem - 0.02px))')

  // Track whether portal has been registered (no state needed - just prevent double registration)
  const portalRegisteredRef = useRef(false)

  /**
   * Callback ref that registers the portal container with Primer's portal system.
   * Only used when in mobile mode (narrow viewport).
   *
   * Problem: When this component is rendered inside a Dialog (e.g., on mobile),
   * the ActionMenu dropdown appears behind the dialog because Primer's default
   * portal root (#__primerPortalRoot__) uses `position: absolute` while the
   * Dialog uses `position: fixed`, creating incompatible stacking contexts.
   *
   * Solution: Register a local portal container that lives inside the dialog's
   * stacking context. By using a callback ref (not useEffect), we ensure the
   * portal is registered synchronously on mount—before any user interaction.
   * This avoids race conditions and eliminates the need for state updates.
   */
  const portalContainerCallback = useCallback((node: HTMLDivElement | null) => {
    if (node && !portalRegisteredRef.current) {
      registerPortalRoot(node, PORTAL_CONTAINER_NAME)
      portalRegisteredRef.current = true
    }
  }, [])

  // Context value is stable - always provides the portal name
  const portalContextValue = useMemo(() => ({portalContainerName: PORTAL_CONTAINER_NAME}), [])

  const renderStatusItem = (option: StatusOption) => {
    const count = statusCounts[option.value]
    return (
      <ActionList.Item
        key={option.value}
        selected={selectedFilter === option.value}
        onSelect={() => onFilterChange(option.value)}
      >
        <ActionList.LeadingVisual>
          <option.icon />
        </ActionList.LeadingVisual>
        {option.label}
        {count > 0 && (
          <ActionList.TrailingVisual>
            <CounterLabel>{count}</CounterLabel>
          </ActionList.TrailingVisual>
        )}
      </ActionList.Item>
    )
  }

  const menuContent = (
    <ActionMenu>
      <ActionMenu.Anchor>
        <IconButton size="small" icon={FilterIcon} aria-label="Filter by job status" />
      </ActionMenu.Anchor>
      <ActionMenu.Overlay width="medium">
        <ActionList selectionVariant="single">
          <ActionList.GroupHeading>Filter by status</ActionList.GroupHeading>

          {/* All */}
          <ActionList.Item selected={selectedFilter === 'all'} onSelect={() => onFilterChange('all')}>
            <ActionList.LeadingVisual>
              <TableIcon />
            </ActionList.LeadingVisual>
            All
            {statusCounts['all'] > 0 && (
              <ActionList.TrailingVisual>
                <CounterLabel>{statusCounts['all']}</CounterLabel>
              </ActionList.TrailingVisual>
            )}
          </ActionList.Item>

          <ActionList.Divider />

          {/* Pending statuses: Queued, In progress, Action required */}
          {pendingStatuses.map(renderStatusItem)}

          <ActionList.Divider />

          {/* Success */}
          {successStatuses.map(renderStatusItem)}

          <ActionList.Divider />

          {/* Failure statuses: Failed, Cancelled, Skipped, Timed out */}
          {failureStatuses.map(renderStatusItem)}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )

  // When in narrow viewport (where jobs list is in a dialog),
  // wrap with portal context to fix stacking issues
  if (isNarrow) {
    return (
      <>
        {/*
          Local portal container for the ActionMenu overlay.
          By default, Primer renders overlays into a global portal root with position: absolute,
          which creates a stacking context conflict when inside a Dialog (position: fixed).
          This div serves as a custom portal root within the same stacking context as the dialog,
          ensuring the dropdown renders on top of the dialog content on narrow/mobile screens.
        */}
        <div ref={portalContainerCallback} />
        <PortalContext value={portalContextValue}>{menuContent}</PortalContext>
      </>
    )
  }

  // Default rendering without portal context override (desktop mode)
  return menuContent
})
